import React, { useState, useEffect } from 'react';
import {Button, Drawer, Badge, Collapse, Empty, Spin, Tag, message} from 'antd';
import { BellOutlined, InfoCircleOutlined, WarningOutlined, CloseCircleOutlined, BulbOutlined, ReadOutlined } from '@ant-design/icons';
import { fetchWrapper } from '../../helpers';
import { useTranslation } from 'react-i18next';
import XP_UP_ICON from "../../pages/icons/xp-up-icon.png";
import fbIcon from "../../pages/icons/facebook-icon.svg";
import linkedInIcon from "../../pages/icons/linkedin-icon.svg";
import instagramIcon from "../../pages/Home/icons/instagram-icon.svg";
import {useOrganization} from "../../context/organizationContext";
import {Link, useParams} from "react-router-dom";
import {ConfettiExplosion} from "react-confetti-explosion";

const { Panel } = Collapse;
const baseUrl = `${process.env.REACT_APP_API_URL}`;

const NotificationDrawer = () => {
	const [open, setOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();
	const fetchNotifications = async () => {
		setLoading(true);
		try {
			const unreadResponse = await fetchWrapper.get(`${baseUrl}/notifications?status=unread&page=1&limit=100`);
			const unreadNotifications = (unreadResponse.notifications || []).map(notification => ({
				...notification,
				isUnread: true
			}));

			// if (unreadResponse.count) {
			// 	message.info(t('notifications.yourHaveUnreadNotifications'));
			// }

			const readResponse = await fetchWrapper.get(`${baseUrl}/notifications?status=read&page=1&limit=50`);
			const readNotifications = (readResponse.notifications || []).map(notification => ({
				...notification,
				isUnread: false
			}));

			setNotifications([...unreadNotifications, ...readNotifications]);
			setUnreadCount(unreadResponse.count || 0);
		} catch (error) {
			console.error('Error fetching notifications:', error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchNotifications();

		const intervalId = setInterval(() => {
			fetchNotifications();
		}, 1000 * 60 * 30);
		
		return () => clearInterval(intervalId);
	}, []);

	const showDrawer = () => {
		setOpen(true);
	};

	const onClose = () => {
		setOpen(false);
	};

	const markAsRead = async (notificationId) => {
		try {
			const response = await fetchWrapper.patch(`${baseUrl}/notifications/status/${notificationId}`);
			if (response.message === "Successfully updated notification.") {
				setNotifications(prev => 
					prev.map(notification => {
						if (notification.notificationId === notificationId) {
							return {
								...notification,
								status: 'read',
								isUnread: false
							};
						}
						return notification;
					})
				);
				setUnreadCount(prev => prev - 1);
			}
		} catch (error) {
			console.error('Error marking notification as read:', error);
		}
	};

	// Усечение заголовка до 30 символов
	const truncateTitle = (text) => {
		if (!text) return '';
		const max = 25;
		return text.length > max ? text.slice(0, max) + '...' : text;
	};

	const getNotificationIcon = (type, theme) => {
		if (type === 'info') {
			return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
		} else if (type === 'warning') {
			return <WarningOutlined style={{ color: '#faad14' }} />;
		} else if (type === 'danger') {
			return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
		}

		return <ReadOutlined style={{ color: '#1890ff' }} />;
	};
	
	const getThemeTag = (theme) => {
		if (theme === 'alert') {
			return <Tag color="orange">{t('notifications.themes.alert')}</Tag>;
		}
		if (theme === 'suggestion') {
			return <Tag color="green">{t('notifications.themes.suggestion')}</Tag>;
		}
		if (theme === 'create_post') {
			return <Tag color="purple">{t('notifications.themes.create_post')}</Tag>;
		}
		if (theme === 'approved') {
			return <Tag color="success">{t('notifications.themes.approved')}</Tag>;
		}
		if (theme === 'rejected') {
			return <Tag color="error">{t('notifications.themes.rejected')}</Tag>;
		}
		if (theme === 'new_level' || theme === 'new_achievement') {
			return <img className='w-7 h-7 object-contain' src={XP_UP_ICON} alt={theme}/>;
		}
		return null;
	};
	
	const getSocialNetworkIcon = (socialNetwork) => {
		if (!socialNetwork) return null;
		
		const network = String(socialNetwork).toLowerCase();
		
		if (network.includes('facebook')) {
			return <img style={{ minWidth: 20, marginLeft: 5, marginRight: 5 }} width={20} src={fbIcon} alt="Facebook" />;
		}
		if (network.includes('linkedin')) {
			return <img style={{ minWidth: 23, marginLeft: 5, marginRight: 5 }} width={20} src={linkedInIcon} alt="LinkedIn" />;
		}
		if (network.includes('instagram')) {
			return <img style={{ minWidth: 20, marginLeft: 5, marginRight: 5 }} width={20} src={instagramIcon} alt="Instagram" />;
		}
		
		return null;
	};

	// New helper to render multiple icons
	const renderSocialNetworkIcons = (notification) => {
		const arr = Array.isArray(notification.socialNetworks)
			? notification.socialNetworks
			: (notification.socialNetwork ? String(notification.socialNetwork).split(',').map(s => s.trim()).filter(Boolean) : []);
		if (!arr.length) return null;
		return (
			<div style={{ display: 'flex', alignItems: 'center' }}>
				{arr.map(sn => (
					<React.Fragment key={sn}>{getSocialNetworkIcon(sn)}</React.Fragment>
				))}
			</div>
		);
	};

	const renderNotifications = () => {
		if (notifications.length === 0) {
			return <Empty description={t('notifications.allEmpty')} />;
		}

		return (
			<Collapse
				size='large'
				rootClassName='!rounded-none'
				className='!rounded-none'
				accordion={true}
				onChange={(keys) => {
					if (keys.length > 0) {
						keys.forEach(key => {
							const notification = notifications.find(n => n.notificationId === parseInt(key));
							if (notification && notification.isUnread) {
								markAsRead(notification.notificationId);
							}
						});
					}
				}}
			>
				{notifications.map(notification => (
					<Panel
							style={{
								width: '100%',
								paddingTop: 5,
								paddingBottom: 5,
								backgroundColor: notification.isUnread ? '#f2eff6' : '#fff',
							}}
						key={notification.notificationId}
						header={
							<div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
								{getNotificationIcon(notification.type)}
								<span
									style={{
										marginLeft: 8,
										flex: 1,
										minWidth: 0,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										fontWeight: notification.isUnread ? 'bold' : 'normal',
										color: notification.isUnread ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.65)'
									}}
									title={notification.title}
								>
									{truncateTitle(notification.title)}
								</span>
								<div style={{ marginLeft: 8, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
									{renderSocialNetworkIcons(notification)}
									{getThemeTag(notification.theme)}
								</div>
							</div>
						}
						className={`notification-panel notification-${notification.type}`}
					>
						<div className="notification-content">
							{(notification.theme === "new_level" || notification.theme === "new_achievement") && (
									<ConfettiExplosion
											force={0.8}
											duration={3000}
											particleCount={250}
											width={1600}
											zIndex={9999999}
									/>
							)}
							<h5 className='text-lg font-semibold'>{notification.title}</h5>
							<p className='mt-1 mb-5'>
								{notification.description}
							</p>
							{(notification.theme === "new_level" || notification.theme === "new_achievement") && (
									<Link
											to={`/achievements`}
											className='block logout-button !my-2.5 hover:text-black text-center'
									>
										{t("achievements.goToMyAchievements")}
									</Link>
							)}
							{(notification.contentPlanId || notification.postId) && (notification.theme === "create_post" || notification.theme === "approved" || notification.theme === "rejected" || notification.theme === "alert") && (
									window.location.pathname.includes('create-post')
										?
									<Link
										to={`/create-post/${notification.contentPlanId || notification.postId}`}
										onClick={(e)=>{
											e.preventDefault();
											window.location.pathname = `/create-post/${notification.contentPlanId || notification.postId}`
										}}
										className='block logout-button !my-2.5 hover:text-black text-center'
									>
										{t("notifications.goToPost")}
									</Link>
										:
									<Link
											to={`/create-post/${notification.contentPlanId || notification.postId}`}
											className='block logout-button !my-2.5 hover:text-black text-center'
									>
										{t("notifications.goToPost")}
									</Link>
							)}
							<div className="text-black/25 text-xs flex justify-between items-center">
								<span>{notification.author}</span>
								<span>{new Date(notification.createdAt).toLocaleString()}</span>
							</div>
						</div>
					</Panel>
				))}
			</Collapse>
		);
	};

	return (
		<>
			<Badge offset={[-5, 5]} count={unreadCount} size="small">
				<Button
					className={`${unreadCount > 0 ? 'animate-half-rotate' : ''} text-white hover:!bg-transparent !rounded-full`}
					type="text" 
					icon={<BellOutlined style={{ fontSize: '16px' }} />}
					onClick={showDrawer}
					aria-label={t('notifications.open')}
					style={{ height: 'auto', padding: '8px' }}
				/>
			</Badge>
			<Drawer
				classNames={{
					body: '!p-0',
				}}
				title={t('notifications.title')}
				closable={{ 'aria-label': 'Close Button' }}
				onClose={onClose}
				open={open}
				width={500}
			>
				{(loading && !notifications.length) ? (
					<div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
						<Spin />
					</div>
				) : (
					renderNotifications()
				)}
			</Drawer>
		</>
	);
};

export default NotificationDrawer;